import { memo } from 'react';
import { MoveRight } from 'lucide-react';
import { Link } from '@/hen-ui/atomics/Link';
import type { NavItem } from './types';
import { hasValidHref } from './utils';
import { useRovingTabIndex } from './useRovingTabIndex';

interface DesktopSidebarProps {
  title: string;
  sections: NavItem[];
  activeIndex: number;
  onActiveChange: (index: number) => void;
}

export const DesktopSidebar = memo(function DesktopSidebar({
  title,
  sections,
  activeIndex,
  onActiveChange,
}: DesktopSidebarProps) {
  const { getItemProps, containerProps } = useRovingTabIndex({
    activeIndex,
    itemCount: sections.length,
    onActiveChange,
  });

  return (
    <aside className='shrink-0 rounded-l-nav-panel bg-neutral-50 py-lg lg:py-xl xl:w-subnav-sidebar xl:py-2xl'>
      <div className='px-lg py-xxs text-b3 font-bold text-subtext lg:px-sm lg:py-xxs lg:text-b4 xl:pl-xl xl:pr-lg xl:py-xxs xl:text-h4'>
        {title}
      </div>
      <nav
        aria-label='Sub-navigation sections'
        className='mt-sm flex flex-col xl:mt-lg'
        {...containerProps}
      >
        {sections.map((section, index) => {
          const isLink = hasValidHref(section);
          const isActive = index === activeIndex;

          // The Link default variant sets `hover:text-link-hover`. Pin the
          // hover color to the current text color so only the underline
          // changes on hover. Harmless on the button branch (no competing
          // hover:text rule).
          const activeClass = isActive
            ? 'bg-primary-700 text-white hover:text-white'
            : 'text-subtext bg-transparent hover:text-subtext';
          const baseClass = `flex w-full cursor-pointer items-center justify-between px-lg py-md font-bold transition-colors duration-fast ease-in-out lg:px-sm lg:py-xs xl:pl-xl xl:pr-lg xl:py-sm rounded-xs focus-visible:ring-focus focus-visible:ring-2 focus-visible:ring-offset-focus focus-visible:ring-offset-1 ${activeClass}`;

          const itemProps = getItemProps(index);

          if (isLink) {
            return (
              <Link
                key={section.id}
                href={section.uri}
                underlineStyle='thin'
                className={baseClass}
                tabIndex={itemProps.tabIndex}
                ref={itemProps.ref}
                onMouseEnter={itemProps.onMouseEnter}
                onFocus={itemProps.onFocus}
                aria-current={isActive ? 'true' : undefined}
              >
                <span className='text-b3 font-bold lg:text-b4 xl:text-b3'>
                  {section.label}
                </span>
                <MoveRight
                  aria-hidden='true'
                  size={24}
                  className={`ml-xs flex-shrink-0 ${
                    isActive ? 'text-white' : 'text-subtext'
                  }`}
                />
              </Link>
            );
          }

          return (
            <button
              key={section.id}
              type='button'
              className={`text-left ${baseClass}`}
              tabIndex={itemProps.tabIndex}
              ref={itemProps.ref}
              onMouseEnter={itemProps.onMouseEnter}
              onFocus={itemProps.onFocus}
              onClick={() => onActiveChange(index)}
              aria-current={isActive ? 'true' : undefined}
            >
              <span className='text-b3 lg:text-b4 xl:text-b3'>
                {section.label}
              </span>
              <MoveRight
                aria-hidden='true'
                size={24}
                className={`ml-xs flex-shrink-0 ${
                  isActive ? 'text-white' : 'text-subtext'
                }`}
              />
            </button>
          );
        })}
      </nav>
    </aside>
  );
});
